import React, { useState, useEffect } from "react";
import Pusher from "pusher-js";
import {
  UploadCloud,
  FileUp,
  Scan,
  ShieldCheck,
  CheckCircle2,
  Eye,
  RefreshCw,
  Trash2,
  Info,
  Plus,
  Sparkles,
  Loader2,
  Loader,
  AlertCircle,
} from "lucide-react";
import { toastr } from "react-redux-toastr";
import {
  useAgregarDocumentoMutation,
  useCargaDocumentoMutation,
  useEditarDocumentoMutation,
  useEliminardocAdiMutation,
  useLazyGetDocsListQuery,
} from "../services/docs";

export const ItemDocumentoCarga = ({
  doc,
  i,
  siniestro,
  tramite_id,
  doc_adicional,
}) => {
  // Estado local para la UI que simula las tarjetas
  const [docState, setDocState] = useState(
    doc.documento &&
      doc.documento.length > 0 &&
      doc.documento[0].fi_rechazo_digital === 0
      ? "completed"
      : "idle"
  );
  const [docCargado, setDocGargadoState] = useState(
    doc.documento && doc.documento.length > 0 && doc.documento[0]
  );
  useEffect(() => {
    if (docCargado) {
      if (docCargado.fi_rechazo_digital === 2) { setDocState("rechazado") }
      if (docCargado.fi_rechazo_digital === 1) { setDocState("verificado") }
    }
  }, [docCargado])

  const [aiData, setAiData] = useState<any>(null);

  const [cargaSend] = useCargaDocumentoMutation();
  const [tigger] = useLazyGetDocsListQuery();
  const [eliminar, { isLoading: cargandoEliminado }] = useEditarDocumentoMutation();
  const [eliminarAdicional] = useEliminardocAdiMutation();

  useEffect(() => {
    const docActual = doc.documento && doc.documento.length > 0 ? doc.documento[0] : null;

    if (docActual) {
      if (docActual.fi_rechazo_digital === 2) {
        setDocState("rechazado");
      } else if (docActual.fi_rechazo_digital === 1) {
        setDocState("verificado");
      } else if (docActual.fi_rechazo_digital === 0) {
        // Solo lo marcamos como completado si no lo estábamos procesando, 
        // o si Pusher falló y la BD ya confirma que existe y está aprobado.
        setDocState((prev) =>
          ["uploading", "ocr", "validating"].includes(prev) ? "completed" : "completed"
        );
      }
    }
  }, [doc]);

  useEffect(() => {
    let intervalId;
    const estadosProcesando = ["uploading", "ocr", "validating"];

    // Si la tarjeta está en modo "cargando", iniciamos un temporizador
    if (estadosProcesando.includes(docState)) {
      intervalId = setInterval(() => {
        console.log("Pusher no ha respondido. Consultando estado en la BD...");

        // Disparamos la consulta a la BD. 
        // Si ya terminó de procesarse, esto actualizará el prop 'doc',
        // lo que activará el useEffect de arriba y cambiará 'docState' a completado/rechazado.
        tigger({ siniestro, tramite: tramite_id });

      }, 5000); // Consulta cada 5 segundos (puedes subirlo a 10000 para 10 seg)
    }

    // Función de limpieza: Se ejecuta automáticamente en cuanto docState
    // cambie a "completed", "error" o "rechazado", matando el ciclo del setInterval.
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [docState, tigger, siniestro, tramite_id]);
  useEffect(() => {
    const pusher = new Pusher(import.meta.env.VITE_PUBLIC_APP_PUSHER_KEY || "b6af0b458098e4705ad5", {
      cluster: import.meta.env.VITE_PUBLIC_APP_PUSHER_CLUSTER || "us3",
      forceTLS: true,
    });

    const channel = pusher.subscribe(`tramite-${tramite_id}`);

    channel.bind("estado-documento", (data: any) => {
      // Solo actualizamos si el evento es para este documento específico
      if (data.documento_id === doc.id) {
        setDocState(data.estado);

        // Guardamos la información extraída de la IA si viene en el evento
        if (data.ai_data) {
          setAiData(data.ai_data);
        }

        if (data.estado === "completed") {
          tigger({ siniestro, tramite: tramite_id }); // Refrescar lista de BD
        } else if (data.estado === "error") {
          // No pasamos a "idle", mostramos pantalla de error
          toastr.warning("El documento fue revisado pero tiene observaciones.");
        }
      }
    });

    return () => {
      channel.unbind_all();
      channel.unsubscribe();
      pusher.disconnect();
    };
  }, [tramite_id, doc.id, tigger, siniestro]);

  // ==========================================
  // LÓGICA DE CARGA DE ARCHIVO
  // ==========================================
  const onChange = (e: any) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reiniciar datos de IA y cambiar estado a uploading de inmediato
    setAiData(null);
    setDocState("uploading");

    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      const fileString = reader.result;

      try {
        await cargaSend({
          documento: fileString,
          documento_id: doc.id,
          siniestro: siniestro,
          tramite_id: Number(tramite_id),
          adicional: doc.adicional,
        }).unwrap();
      } catch (err: any) {
        setDocState("idle");
        if (err.data?.message) {
          const msg = Array.isArray(err.data.message)
            ? err.data.message[0]
            : err.data.message;
          toastr.error(msg);
        }
      }
    };
  };

  const eliminarDocumentoAction = async () => {
    setDocState("idle");
    setAiData(null);
    if (doc.adicional === 1) {
      await eliminarAdicional({
        fc_siniestro: siniestro,
        tramite_id: +tramite_id,
        id: doc.id,
      }).unwrap();
    } else {
      await eliminar({
        fc_siniestro: siniestro,
        fi_tramite_id: +tramite_id,
        fi_documento_id: doc.id,
        fi_documento_activo: 0,
      }).unwrap();
    }
    tigger({ siniestro, tramite: tramite_id });
  };

  // ==========================================
  // RENDERIZADO CONDICIONAL POR ESTADOS
  // ==========================================

  // Clases dinámicas según el estado
  const containerClass =
    docState === "idle"
      ? "bg-white border-dashed border-slate-300 hover:border-blue-400"
      : docState === "uploading"
        ? "bg-white border-blue-300"
        : docState === "ocr"
          ? "bg-white border-2 border-indigo-500 shadow-md"
          : docState === "validating"
            ? "bg-white border-2 border-amber-500 shadow-md"
            : docState === "error"
              ? "bg-white border-2 border-red-400 shadow-md"
              : docState === "verificado"
                ? "bg-white border-2 border-brand-500 shadow-md"
                : docState === "rechazado"
                  ? "bg-white border-2 border-amber-500 shadow-md"
                  : "bg-white border border-emerald-200 shadow-sm";

  const topBarClass =
    docState === "idle"
      ? "bg-slate-200"
      : docState === "uploading"
        ? "bg-blue-500 shimmer-effect relative overflow-hidden"
        : docState === "ocr"
          ? "bg-indigo-600 shimmer-effect relative overflow-hidden"
          : docState === "validating"
            ? "bg-amber-500"
            : docState === "error"
              ? "bg-red-500"
              : docState === "verificado"
                ? "bg-brand-500"
                : docState === "rechazado"
                  ? "bg-amber-500"
                  : "bg-emerald-500";

  console.log(docState);
  return (
    <div
      className={`duration-300 relative flex min-h-[300px] flex-col overflow-hidden rounded-2xl border transition-all ${containerClass}`}
    >
      {/* Barra superior */}
      <div className={`h-1.5 w-full ${topBarClass}`}></div>

      {/* Header Común */}
      <div className="p-5 pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold
              ${docState === "idle"
                  ? "bg-slate-100 text-slate-700"
                  : docState === "ocr"
                    ? "bg-indigo-100 text-indigo-700"
                    : docState === "validating"
                      ? "bg-amber-100 text-amber-800"
                      : docState === "error"
                        ? "bg-red-100 text-red-700"
                        : docState === "completed"
                          ? "bg-emerald-100 text-emerald-700"
                          : docState === "rechazado"
                            ? "bg-amber-100 text-amber-500"
                            : docState === "verificado"
                              ? "bg-brand-100 text-brand-500"
                              : "bg-slate-100 text-slate-700"
                }`}
            >
              {doc.fi_documento || i + 1}
            </div>
            <h3 className="text-base font-bold leading-snug text-slate-900">
              {doc.fc_nombre_documento}
            </h3>
          </div>

          {/* Badge de Estatus */}
          <span
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold
            ${docState === "idle"
                ? "border-slate-200 bg-slate-100 text-slate-600"
                : docState === "uploading"
                  ? "border-blue-200 bg-blue-50 text-blue-700"
                  : docState === "ocr"
                    ? "animate-pulse border-indigo-200 bg-indigo-50 text-indigo-700"
                    : docState === "validating"
                      ? "border-amber-200 bg-amber-50 text-amber-800"
                      : docState === "error"
                        ? "border-red-200 bg-red-50 text-red-700"
                        : docState === "verificado"
                          ? "bg-brand-500 text-white"
                          : docState === "rechazado"
                            ? "bg-amber-500"
                            : "border-emerald-200 bg-emerald-50 text-emerald-700"
              }`}
          >
            {!cargandoEliminado && docState === "idle" && "Pendiente"}
            {docState === "uploading" && (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Subiendo
              </>
            )}
            {docState === "ocr" && (
              <>
                <Scan className="h-3.5 w-3.5 animate-spin" /> Generando OCR
              </>
            )}
            {docState === "validating" && (
              <>
                <ShieldCheck className="h-3.5 w-3.5 animate-pulse" /> Validando
                IA
              </>
            )}
            {docState === "error" && (
              <>
                <AlertCircle className="h-3.5 w-3.5" /> Rechazado
              </>
            )}
            {docState === "rechazado" && (
              <>
                <AlertCircle className="h-3.5 w-3.5" /> Rechazado
              </>
            )}
            {docState === "completed" && (
              <>
                <CheckCircle2 className="h-3.5 w-3.5" /> Validado
              </>
            )}
            {docState === "verificado" && (
              <>
                <CheckCircle2 className="h-3.5 w-3.5 text-white" /> Veridicado
              </>
            )}
            {cargandoEliminado && (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Eliminando
              </>
            )}
          </span>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-slate-500">
          {doc.fc_descripcion_documento}
        </p>
      </div>

      {/* BODY DINÁMICO */}
      <div className="flex flex-grow flex-col justify-center px-5 py-2">
        {/* ESTADO: IDLE (Drag & Drop) */}
        {docState === "idle" && (
          <label className="group flex min-h-[170px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-6 text-center transition-all hover:border-blue-500 hover:bg-blue-50/40">
            <input
              type="file"
              className="hidden"
              accept=".pdf,.png,.jpg,.jpeg"
              onClick={(e: any) => (e.target.value = "")}
              onChange={onChange}
            />
            <div className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-blue-600 shadow-sm transition-all group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white">
              <UploadCloud className="h-6 w-6" />
            </div>
            <p className="mt-3 text-xs font-bold text-slate-800 group-hover:text-blue-700">
              Arrastra o haz clic para subir
            </p>
            <p className="mt-1 text-[11px] text-slate-400">PDF, JPG o PNG</p>
          </label>
        )}

        {/* ESTADO: UPLOADING */}
        {docState === "uploading" && (
          <div className="flex min-h-[170px] flex-col justify-center rounded-xl border border-slate-200 bg-slate-50 p-5">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileUp className="h-5 w-5 animate-bounce text-blue-600" />
                <span className="text-xs font-bold text-slate-800">
                  Transmitiendo archivo...
                </span>
              </div>
            </div>
            <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-slate-200">
              <div className="duration-300 h-2 w-[45%] rounded-full bg-blue-600 transition-all"></div>
            </div>
            <p className="flex items-center justify-between text-[11px] text-slate-400">
              <span>Transferencia segura al servidor</span>
            </p>
          </div>
        )}

        {/* ESTADO: OCR */}
        {docState === "ocr" && (
          <div className="relative flex min-h-[170px] flex-col justify-between overflow-hidden rounded-xl bg-slate-900 p-3.5 text-white shadow-inner">
            <div className="animate-scan-laser absolute left-0 right-0 z-20 h-0.5 bg-gradient-to-r from-transparent via-indigo-400 to-transparent shadow-[0_0_12px_#818cf8]"></div>
            <div className="relative z-10 flex items-center justify-between">
              <span className="font-mono flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-indigo-300">
                <Sparkles className="h-3 w-3" /> Extracción de Textos
              </span>
              <span className="rounded border border-indigo-700 bg-indigo-950/80 px-2 py-0.5 text-[10px] text-indigo-200">
                OCR Activo
              </span>
            </div>
            <div className="relative z-10 py-2 text-center">
              <p className="text-xs font-medium text-slate-200">
                Leyendo contenido del documento...
              </p>
            </div>
            <div className="relative z-10 h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
              <div className="h-1.5 w-2/3 rounded-full bg-gradient-to-r from-indigo-400 to-cyan-400"></div>
            </div>
          </div>
        )}

        {/* ESTADO: VALIDATING */}
        {docState === "validating" && (
          <div className="flex min-h-[170px] flex-col justify-between rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="border-b border-slate-200/80 pb-1.5 text-xs font-bold text-slate-800">
              Motor de Inteligencia Artificial:
            </p>
            <div className="space-y-1.5 py-2 text-[11px]">
              <div className="flex items-center justify-between text-emerald-700">
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Estructura del
                  documento
                </span>
                <span className="font-bold">Aprobado</span>
              </div>
              <div className="flex animate-pulse items-center justify-between font-medium text-amber-700">
                <span className="flex items-center gap-1">
                  <Loader className="h-3.5 w-3.5 animate-spin" /> Verificando
                  coherencia y reglas
                </span>
                <span className="font-mono text-[10px]">Analizando...</span>
              </div>
            </div>
          </div>
        )}

        {/* ESTADO: ERROR / RECHAZADO POR IA */}
        {docState === "error" && (
          <div className="flex min-h-[170px] flex-col justify-between rounded-xl border border-red-200 bg-red-50/50 p-4">
            <div className="flex items-start gap-3 mb-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                <AlertCircle className="h-5 w-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-red-800">
                  Observaciones en el documento
                </h4>
                <p className="mt-1 text-xs leading-relaxed text-red-700">
                  {aiData?.razon ||
                    "El documento no cumple con los requisitos mínimos."}
                </p>
              </div>

            </div>

            <>
              <button
                onClick={() => {
                  setDocState("idle");
                  setAiData(null);
                }}
                className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-100"
              >
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Intentar de nuevo
              </button>
            </>

            {/* Bloque Dinámico: Muestra lo que sí logró extraer */}
            {aiData?.extraccion && (
              <div className="mt-4 rounded-lg border border-red-100 bg-white p-3 shadow-sm">
                <p className="mb-2 border-b border-slate-100 pb-1.5 text-[11px] font-semibold text-slate-700">
                  Datos detectados por el OCR:
                </p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {Object.entries(aiData.extraccion).map(([key, value]) => (
                    <div key={key}>
                      <span className="block text-[9px] uppercase tracking-wider text-slate-400">
                        {key.replace(/_/g, " ")}
                      </span>
                      <span
                        className={`text-[11px] font-medium ${value === "NINGUNO"
                          ? "font-bold text-red-500"
                          : "text-slate-800"
                          }`}
                      >
                        {String(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {docState === "rechazado" && (
          <div className="flex min-h-[170px] flex-col justify-between rounded-xl border border-red-200 bg-red-50/50 p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                <AlertCircle className="h-5 w-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-amber-800">
                  Observaciones en el documento
                </h4>
                <p className="mt-1 text-xs leading-relaxed text-amber-700">
                  {docCargado.fc_comentarios_rechazo}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ESTADO: COMPLETED */}
        {docState === "completed" && (
          <div className="relative flex min-h-[170px] flex-col justify-between rounded-xl border border-slate-200 bg-slate-50/80 p-3">
            <div className="flex items-start gap-3">
              <div className="flex h-14 w-12 shrink-0 flex-col items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700">
                <FileUp className="h-6 w-6" />
                <span className="mt-0.5 text-[9px] font-bold">DOC</span>
              </div>
              <div className="min-w-0 flex-grow">
                <p className="truncate text-xs font-bold text-slate-800">
                  Documento Validado
                </p>
                <div className="mt-1.5 border-t border-slate-200 pt-1.5">
                  <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
                    <ShieldCheck className="h-3.5 w-3.5" /> Revisión IA Exitosa
                  </span>
                </div>
              </div>
            </div>

            {/* Bloque Dinámico: Muestra la extracción EXITOSA de la IA */}
            {aiData?.extraccion ? (
              <div className="mt-3 rounded-lg border border-emerald-100 bg-white p-2.5 shadow-sm">
                <p className="mb-1.5 border-b border-slate-100 pb-1 text-[10px] font-semibold text-slate-700">
                  Datos validados por OCR:
                </p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                  {Object.entries(aiData.extraccion).map(([key, value]) => (
                    <div key={key}>
                      <span className="block text-[8px] uppercase tracking-wider text-slate-400">
                        {key.replace(/_/g, " ")}
                      </span>
                      <span
                        className="block truncate text-[10px] font-medium text-slate-800"
                        title={String(value)}
                      >
                        {String(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* Fallback por si es un documento que no usa OCR (ej. adicional sin IA) */
              <div className="mt-3 flex items-center justify-between rounded-lg border border-emerald-200/70 bg-emerald-50 p-2 text-[11px] text-emerald-800">
                <span>Proceso completado</span>
                <span className="font-bold">100% Match</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* FOOTER */}
      <div className="mt-auto flex items-center justify-between gap-2 border-t border-slate-100 bg-slate-50/40 p-4">
        {docState === "idle" && (
          <>
            <span className="flex items-center gap-1 text-[11px] text-slate-400">
              <Info className="h-3.5 w-3.5" /> Requerido
            </span>
            <label className="inline-flex cursor-pointer items-center rounded-lg bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-blue-700">
              <Plus className="mr-1 h-3.5 w-3.5" /> Seleccionar
              <input
                type="file"
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg"
                onClick={(e: any) => (e.target.value = "")}
                onChange={onChange}
              />
            </label>

            <button
              onClick={eliminarDocumentoAction}
              className="rounded-lg border border-slate-200 p-2 text-slate-600 transition-colors hover:bg-red-50 hover:text-red-600"
              title="Eliminar archivo"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </>
        )}

        {(docState === "uploading" ||
          docState === "ocr" ||
          docState === "validating") && (
            <span className="w-full text-center text-[11px] italic text-slate-500">
              Aguarde un momento, procesando automáticamente...
            </span>
          )}

        {/* Footer para estado de error */}
        {docState === "error" || docState === "rechazado" && (
          <>
            <span className="flex items-center gap-1 text-[11px] font-medium text-red-500">
              Requiere corrección
            </span>
            <button
              onClick={() => {
                setDocState("idle");
                setAiData(null);
              }}
              className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-100"
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Intentar de nuevo
            </button>
          </>
        )}


        {docState === "completed" && (
          <>
            {doc.documento?.[0]?.fc_ruta_documento && (
              <a
                href={doc.documento[0].fc_ruta_documento}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100"
              >
                <Eye className="mr-1.5 h-3.5 w-3.5" /> Ver documento
              </a>
            )}

            <div className="flex items-center gap-1.5">
              <label
                className="cursor-pointer rounded-lg border border-slate-200 p-2 text-slate-600 transition-colors hover:bg-blue-50 hover:text-blue-600"
                title="Reemplazar archivo"
              >
                <RefreshCw className="h-4 w-4" />
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onClick={(e: any) => (e.target.value = "")}
                  onChange={onChange}
                />
              </label>
            </div>
          </>
        )}
        {docState === "verificado" && (
          <>
            {doc.documento?.[0]?.fc_ruta_documento && (
              <a
                href={doc.documento[0].fc_ruta_documento}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100"
              >
                <Eye className="mr-1.5 h-3.5 w-3.5" /> Ver documento
              </a>
            )}
          </>
        )}
      </div>
    </div>
  );
};
